import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { format } from 'date-fns';

import { useDepartmentsBudgetsReportContext } from './context/DepartmentsBudgetsReportContext';

import PaginationInfo from 'CommonComponents/PaginationInfo';
import PerPageSelect from 'CommonComponents/PerPageSelect';
import TablePagination from 'CommonComponents/TablePagination';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import usePagination from 'Hooks/usePagination';

import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import FileExportIcon from 'Assets/icons/file-export.svg';
import HourglassIcon from 'Assets/icons/hourglass.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const ArchiveDepartmentsBudgetsReport = () => {
    const {
        fetchArchivedReportsData,
        deleteArchivedReport,
        archivedReportsData,
        loadingArchivedReports,
        channelName,
        deleteBudgetReview,
        deleteBudgetReviewAction,
    } = useDepartmentsBudgetsReportContext();

    const [rows, setRows] = useState(archivedReportsData ? archivedReportsData.data : []);

    const pagination = usePagination({ perPage: 10 });

    const confirm = useConfirmationModal();

    useEffect(() => {
        fetchArchivedReportsData({
            page: pagination.page,
            perPage: pagination.perPage,
        });
    }, [pagination.page, pagination.perPage]);

    useEffect(() => {
        if (archivedReportsData) {
            setRows(archivedReportsData.data);
            pagination.setFrom(archivedReportsData.meta.from);
            pagination.setTo(archivedReportsData.meta.to);
            pagination.setTotal(archivedReportsData.meta.total);
            pagination.setLastPage(archivedReportsData.meta.last_page);
        }
    }, [archivedReportsData]);

    // Subscribe to WebSocket for real-time status updates
    useEffect(() => {
        if (!channelName || !window.Echo) {
            return;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.report.generated', (event) => {
            if (event.report) {
                fetchArchivedReportsData({
                    page: pagination.page,
                    perPage: pagination.perPage,
                });
            }
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName, fetchArchivedReportsData, pagination.page, pagination.perPage]);

    const handleReportDelete = useCallback(
        (reportFormat, reportId) => {
            confirm({
                title: $t('Delete Report'),
                message: $t('Are you sure you want to delete this report?'),
                onConfirm: () => {
                    if (reportFormat === 'online') {
                        // For online reports
                        deleteBudgetReview(reportId).then(() => {
                            fetchArchivedReportsData({
                                page: pagination.page,
                                perPage: pagination.perPage,
                            });
                        });
                        return;
                    }

                    deleteArchivedReport(reportId).then(() => {
                        // If we are on a page greater than 1 and the total reports is 1, go back a page
                        if (pagination.page > 1 && pagination.total === 1) {
                            pagination.setPage(pagination.page - 1);
                        } else {
                            fetchArchivedReportsData({
                                page: pagination.page,
                                perPage: pagination.perPage,
                            });
                        }
                    });
                },
            });
        },
        [
            deleteArchivedReport,
            fetchArchivedReportsData,
            pagination.page,
            pagination.perPage,
            confirm,
            deleteBudgetReview,
            pagination.total,
            pagination.setPage,
        ],
    );

    const handleActionDelete = useCallback(
        (reviewId, actionId) => {
            confirm({
                title: $t('Delete Action'),
                message: $t('Are you sure you want to delete this action?'),
                onConfirm: () => {
                    deleteBudgetReviewAction(reviewId, actionId).then(() => {
                        fetchArchivedReportsData({
                            page: pagination.page,
                            perPage: pagination.perPage,
                        });
                    });
                },
            });
        },
        [deleteBudgetReviewAction, fetchArchivedReportsData, pagination.page, pagination.perPage, confirm],
    );

    return (
        <div className="tw-flex tw-flex-col tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary tw-space-y-7 tw-h-full">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                <div className="tw-flex tw-flex-col tw-gap-y-1">
                    <div>
                        <h3 className="table__header">{$t('Archived Reports')}</h3>
                        <div>{$t('The checked columns are included in the report')}</div>
                    </div>
                    <div className="table__pagination-info">
                        <PaginationInfo to={pagination.to} from={pagination.from} total={pagination.total} />
                        <TablePagination
                            lastPage={pagination.lastPage}
                            pageChange={(data) => pagination.setPage(+data.selected + 1)}
                            currentPage={pagination.page}
                        />
                    </div>
                </div>
                <div>
                    <PerPageSelect
                        value={pagination.perPage}
                        options={[10, 25, 50]}
                        onChange={(e) => {
                            pagination.setPerPage(parseInt(e.target.value));
                        }}
                    />
                </div>
            </div>
            <div className="inbud-table_wrapper tw-min-h-0 tw-overflow-auto">
                <table className="inbud-table">
                    <thead className="tw-sticky tw-top-0 tw-z-10">
                        <tr>
                            <th className="tw-w-36">{$t('Date')}</th>
                            <th className="tw-w-40">{$t('Status')}</th>
                            <th className="tw-w-28">{$t('Format')}</th>
                            <th>{$t('Recipient')}</th>
                            <th className="tw-w-20">{$t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingArchivedReports && <TableRowSkeleton rows={10} columns={5} />}
                        {!loadingArchivedReports && rows.length === 0 && (
                            <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                                <td colSpan={5} className="tw-p-4">
                                    <span>{$t('No data available')}</span>
                                </td>
                            </tr>
                        )}
                        {!loadingArchivedReports &&
                            rows.length > 0 &&
                            rows.map((report) => (
                                <React.Fragment key={`${report.id}-${report.date}-${report.statusKey}`}>
                                    {report.format === 'online' ? (
                                        <Fragment>
                                            <OnlineReportRow report={report} onDelete={handleReportDelete} />
                                            {report.children &&
                                                report.children.length > 0 &&
                                                report.children.map((child) => (
                                                    <OnlineReportChildRow
                                                        key={`child-${child.actionId}-${child.statusKey}`}
                                                        report={child}
                                                        onDelete={handleActionDelete}
                                                    />
                                                ))}
                                        </Fragment>
                                    ) : (
                                        <ReportRow report={report} onDelete={handleReportDelete} />
                                    )}
                                </React.Fragment>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ReportRow = ({ report, onDelete }) => {
    const { downloadArchivedReport } = useDepartmentsBudgetsReportContext();

    return (
        <tr>
            <td className="inbud-date-cell">{format(new Date(report.date), 'dd-MM-yyyy HH:mm')}</td>
            <td>{report.statusLabel}</td>
            <td className="tw-capitalize">{report.format}</td>
            <td className="tw-capitalize">{$t('n/a')}</td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    {report.statusKey === 'completed' ? (
                        <button
                            type="button"
                            onClick={() => downloadArchivedReport(report.id)}
                            data-popper-toggle="tooltip"
                            data-placement="left"
                            title="Download"
                        >
                            <ReportStatusIcon status={report.statusKey} />
                        </button>
                    ) : (
                        <ReportStatusIcon status={report.statusKey} />
                    )}
                    {report.canBeDeleted && (
                        <button
                            type="button"
                            onClick={(e) => onDelete(report.format, report.id)}
                            data-popper-toggle="tooltip"
                            data-placement="left"
                            title="Delete"
                        >
                            <TrashIcon className="tw-w-4 tw-h-4 tw-text-red" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
};

const OnlineReportRow = ({ report, onDelete }) => {
    const { showBudgetReviewUrl, editBudgetReviewUrl } = useDepartmentsBudgetsReportContext();

    return (
        <tr className="tw-bg-grey-50">
            <td className="inbud-date-cell">{format(new Date(report.date), 'dd-MM-yyyy HH:mm')}</td>
            <td>{report.statusLabel}</td>
            <td className="tw-capitalize">{report.format}</td>
            <td>{report.reviewerEmail}</td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <a href={showBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', report.id)} target="_blank" rel="noopener noreferrer">
                        <EyeIcon className="tw-h-4 tw-w-4 tw-text-blue" data-popper-toggle="tooltip" data-placement="left" title="Show Budget Review" />
                    </a>
                    {report.canBeEdited && (
                        <a href={editBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', report.id)} target="_blank" rel="noopener noreferrer">
                            <EditIcon className="tw-h-4 tw-w-4 tw-text-green" data-popper-toggle="tooltip" data-placement="left" title={$t('Edit Review')} />
                        </a>
                    )}
                    {report.canBeDeleted && (
                        <button
                            type="button"
                            onClick={(e) => onDelete(report.format, report.id)}
                            data-popper-toggle="tooltip"
                            data-placement="left"
                            title="Delete"
                        >
                            <TrashIcon className="tw-w-4 tw-h-4 tw-text-red" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
};

const OnlineReportChildRow = ({ report, onDelete }) => {
    const { showBudgetReviewUrl } = useDepartmentsBudgetsReportContext();

    return (
        <tr className="!tw-bg-white">
            <td className="inbud-date-cell">{format(new Date(report.date), 'dd-MM-yyyy HH:mm')}</td>
            <td className="!tw-pl-4">{report.statusLabel}</td>
            <td></td>
            <td></td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <a href={showBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', report.id)} target="_blank" rel="noopener noreferrer">
                        <EyeIcon className="tw-h-4 tw-w-4 tw-text-blue" data-popper-toggle="tooltip" data-placement="left" title="Show Budget Review" />
                    </a>
                    {report.canBeDeleted && (
                        <button
                            type="button"
                            onClick={() => onDelete(report.id, report.actionId)}
                            data-popper-toggle="tooltip"
                            data-placement="left"
                            title="Delete"
                        >
                            <TrashIcon className="tw-w-4 tw-h-4 tw-text-red" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
};

const ReportStatusIcon = ({ status }) => {
    if (status === 'in_progress' || status === 'created') {
        return <HourglassIcon className="tw-h-4 tw-w-4 tw-text-yellow" data-popper-toggle="tooltip" data-placement="left" title="Pending" />;
    }

    if (status === 'completed') {
        return <FileExportIcon className="tw-h-4 tw-w-4 tw-text-green" data-popper-toggle="tooltip" data-placement="left" title="Completed" />;
    }

    if (status === 'failed') {
        return <CircleInfoIcon className="tw-h-4 tw-w-4 tw-text-red" data-popper-toggle="tooltip" data-placement="left" title="Failed" />;
    }
    return null;
};

export default ArchiveDepartmentsBudgetsReport;
